export const InquiryStatus = {
  NEW: 'New',
  WAITING_FOR_OFFICE: 'WaitingForOffice',
  WAITING_FOR_USER: 'WaitingForUser',
  ANSWER_RECEIVED: 'AnswerReceived',
  CLOSED: 'Closed',
} as const;

export type InquiryStatusType = (typeof InquiryStatus)[keyof typeof InquiryStatus];

export const InquiryResourceType = {
  SUPPORT_REQUEST: 'SupportRequest',
  SUPPORTED: 'Supported',
  TRANSACTION: 'Transaction',
  STANDING_ORDER: 'StandingOrder',
  GOAL: 'Goal',
  CLASSIFICATION: 'Classification',
  CHILD: 'Child',
} as const;

export type InquiryResourceTypeValue = (typeof InquiryResourceType)[keyof typeof InquiryResourceType];

export const SenderRole = {
  USER: 'User',
  OFFICE: 'Office',
} as const;

export type SenderRoleType = (typeof SenderRole)[keyof typeof SenderRole];

export const NotificationType = {
  MENTION: 'Mention',
  NEW_ASSIGNMENT: 'NewAssignment',
  INTERNAL_NOTE: 'InternalNote',
  SNOOZE_EXPIRED: 'SnoozeExpired',
  CLOSED_REPLY_RECEIVED: 'ClosedReplyReceived',
} as const;

export type NotificationTypeValue = (typeof NotificationType)[keyof typeof NotificationType];

export const ClosedReason = {
  MANUAL: 'Manual',
  AUTO_CLOSE: 'AutoClose',
} as const;

/**
 * The CSS-module class each status chip wears.
 *
 * ⚠️ Replaces a map of raw Material hexes - blue, orange, purple, light purple, grey - which
 * belonged to no palette in this app and read as five unrelated stickers. The scheme now
 * carries meaning in two dimensions, both drawn from the site's own tokens:
 *
 *   hue          WHOSE turn it is    burgundy = the office's, turquoise = the coordinator's
 *   light/dark   how urgent          dark and outlined = needs attention, light = waiting
 *
 * So "חדשה" and "ממתין למענה" are both burgundy because both sit with the office, and the
 * darker of the two is the one nobody has touched yet. Closed is the only grey, because a
 * closed inquiry is the only one that is nobody's turn.
 */
export const InquiryStatusChipClasses: Record<string, string> = {
  [InquiryStatus.NEW]: 'statusNew',
  [InquiryStatus.WAITING_FOR_OFFICE]: 'statusWaitingForOffice',
  [InquiryStatus.WAITING_FOR_USER]: 'statusWaitingForUser',
  [InquiryStatus.ANSWER_RECEIVED]: 'statusAnswerReceived',
  [InquiryStatus.CLOSED]: 'statusClosed',
};

export const InquiryStatusLabels: Record<string, string> = {
  [InquiryStatus.NEW]: 'inquiries.status.new',
  [InquiryStatus.WAITING_FOR_OFFICE]: 'inquiries.status.waitingForOffice',
  [InquiryStatus.WAITING_FOR_USER]: 'inquiries.status.waitingForUser',
  [InquiryStatus.ANSWER_RECEIVED]: 'inquiries.status.answerReceived',
  [InquiryStatus.CLOSED]: 'inquiries.status.closed',
};

export const ResourceTypeLabels: Record<string, string> = {
  [InquiryResourceType.SUPPORT_REQUEST]: 'inquiries.resource.supportRequest',
  [InquiryResourceType.SUPPORTED]: 'inquiries.resource.supported',
  [InquiryResourceType.TRANSACTION]: 'inquiries.resource.transaction',
  [InquiryResourceType.STANDING_ORDER]: 'inquiries.resource.standingOrder',
  [InquiryResourceType.GOAL]: 'inquiries.resource.goal',
  [InquiryResourceType.CLASSIFICATION]: 'inquiries.resource.classification',
  [InquiryResourceType.CHILD]: 'inquiries.resource.child',
};
